import React, { FC, useEffect, useState } from 'react';

import { LinearProgress } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import styles from './ContextModelWordMapping.module.scss';
import { dataGridFocusStyles } from 'styles';
import { WordMappingModel } from '../WordMappingModel';
import { wordMappingColumns } from './WordMappingTable/WordMappingTableDefinition';
import { WordMappingCustomToolbar } from './WordMappingTable/WordMappingCustomToolbar';
import EditCodeDialog from './EditCodeDialog/EditCodeDialog';
import { ContextManagerService } from 'pages/ContextManagerPage/ContextManagerService';
import { AlertConfigModel } from 'dialogs/AlertDialog/AlertConfigModel';
import AlertDialog from 'dialogs/AlertDialog/AlertDialog';

interface ContextModelWordMappingProps {
  isParentFetching: boolean;
  wordMappings: WordMappingModel[];
  onUpdate: () => void;
}

const ContextModelWordMapping: FC<ContextModelWordMappingProps> = (
  {
    isParentFetching,
    wordMappings,
    onUpdate
  },
) => {

  const [currentAlert, setCurrentAlert] = useState<AlertConfigModel | null>(null);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState<boolean>(false);
  const [isEditWordMappingDialogOpen, setIsEditWordMappingDialogOpen] = useState<boolean>(false)
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [selectedWordMapping, setSelectedWordMapping] = useState<WordMappingModel | null>(null);
  const [selectedField, setSelectedField] = useState<'code_S' | 'code_SS' | 'code_COM' | null>(null);

  const [rdsPsSystemCodes, setRdsPsSystemCodes] = useState<string[] | null>(null)
  const [rdsPsSubSystemCodes, setRdsPsSubSystemCodes] = useState<string[] | null>(null)
  const [rdsPsComponentCodes, setRdsPsComponentCodes] = useState<string[] | null>(null)

  useEffect(() => {
    const getRDSPSDocumentation = async (): Promise<void> => {
      setIsFetching(true);
      try {
        const fetchedRdsPsDocs: any = await ContextManagerService.getRDSPSDocumentation();

        if (!fetchedRdsPsDocs) {
          throw new Error('No RDS-PS Docs returned by service');
        }

        if (fetchedRdsPsDocs?.systems) {
          const systemCodes: string[] = Object.keys(fetchedRdsPsDocs.systems).filter(code => code !== 'X');
          setRdsPsSystemCodes(systemCodes);
        }

        if (fetchedRdsPsDocs?.subsystems) {
          const { subsystems } = fetchedRdsPsDocs;
          let subSystemCodes: string[] = [];
          for (const key in subsystems) {
            if (subsystems.hasOwnProperty(key) && subsystems[key].hasOwnProperty('L2')) {
              const L2Keys = Object.keys(subsystems[key].L2);
              subSystemCodes = subSystemCodes.concat(L2Keys).filter(code => code !== 'XX');
            }
          }

          setRdsPsSubSystemCodes(subSystemCodes);
        }

        if (fetchedRdsPsDocs.components) {
          const { components } = fetchedRdsPsDocs;
          let componentCodes: string[] = [];
          for (const topLevelKey in components) {
            if (components.hasOwnProperty(topLevelKey)) {
              const L2Objects = components[topLevelKey].L2;
              if (L2Objects) {
                // Iterate through each L2 object
                for (const L2Key in L2Objects) {
                  if (L2Objects.hasOwnProperty(L2Key)) {
                    const L3Objects = L2Objects[L2Key].L3;
                    if (L3Objects) {
                      // Collect keys from L3 objects
                      const L3Keys = Object.keys(L3Objects);
                      componentCodes = componentCodes.concat(L3Keys).filter(code => code !== 'XXX');
                    }
                  }
                }
              }
            }
          }

          setRdsPsComponentCodes(componentCodes);
        }
      } catch (error) {
        setRdsPsSystemCodes(null);
        setRdsPsSubSystemCodes(null);
        setRdsPsComponentCodes(null);
        console.error('Failed getting powerplant sensor mappings');
        const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed getting powerplant sensor mappings. Please refresh, and contact support if the issue persists.'};
        setCurrentAlert(alertConfig);
        setIsAlertDialogOpen(true);
      } finally {
        setIsFetching(false);
      }
    }

    getRDSPSDocumentation();
  }, []);

  const closeEditWordMappingDialog = (): void => {
    setSelectedWordMapping(null);
    setSelectedField(null);
    setIsEditWordMappingDialogOpen(false);
  }

  const openEditWordMappingDialog = (row: WordMappingModel, field: 'code_S' | 'code_SS' | 'code_COM'): void => {
    setSelectedWordMapping(row);
    setSelectedField(field);
    setIsEditWordMappingDialogOpen(true);
  }

  const isAnyFetching: boolean = isParentFetching || isFetching;

  return (
    <div
      className={styles.ContextModelWordMapping}
      data-testid="ContextModelWordMapping"
    >
      {isEditWordMappingDialogOpen && selectedField && selectedWordMapping &&
        <EditCodeDialog
          field={selectedField}
          onClose={closeEditWordMappingDialog}
          onCodeUpdate={onUpdate}
          rdsPsSystemCodes={rdsPsSystemCodes}
          rdsPsSubSystemCodes={rdsPsSubSystemCodes}
          rdsPsComponentCodes={rdsPsComponentCodes}
          wordMapping={selectedWordMapping}
        />
      }

      <DataGrid
        rows={wordMappings}
        columns={wordMappingColumns(openEditWordMappingDialog)}
        slots={{
          loadingOverlay: LinearProgress,
          toolbar: () => WordMappingCustomToolbar(),
        }}
        loading={isAnyFetching}
        className="project-data-grid"
        getRowId={row => row._id}
        sx={dataGridFocusStyles}
        // sx={{ ...dataGridFocusStyles, ...dataGridFontSizeStyles }}
        disableRowSelectionOnClick
        disableColumnMenu
        rowHeight={44}
        // pageSizeOptions={[]}
        // showCellVerticalBorder
        // showColumnVerticalBorder
        // onCellClick={(params, event, details) => cellClickHandler(params)}
      />

      { isAlertDialogOpen && currentAlert &&
        <AlertDialog
          onClose={() => {
            setIsAlertDialogOpen(false);
            setCurrentAlert(null);
          }}
          title={currentAlert.title}
          message={currentAlert.message}
        ></AlertDialog>
      }
    </div>
  );
}

export default ContextModelWordMapping;
